<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>Filter Configurations</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="filters"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, 100],
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        :items-per-page="itemsPerPage"
        :server-items-length="totalFilters"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:item.settings="{ item }">
          <div v-for="([key, value], index) in filteredSettings(item.settings)" :key="index">
            <strong>{{ key }}:</strong> {{ Array.isArray(value) ? value.join(' - ') : value }} <br />
          </div>
        </template>
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="600px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Filter</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row dense>
                        <v-col cols="12">
                          <v-select
                            v-model="selectedFilterType"
                            :items="filtersAvailable"
                            item-text="label"
                            item-value="name"
                            label="Filter Type*"
                            @change="onFilterTypeChange"
                            required
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-label>{{ currentDescription }}</v-label>
                        </v-col>
                        <template v-for="(field, key) in currentFields">
                          <v-col :key="key" cols="12">
                            <v-text-field
                              v-if="field.value.type === 'text'"
                              v-model="editedItem.fields[field.name]"
                              :label="field.label"
                              :hint="field.description"
                              persistent-hint
                            ></v-text-field>
                            <v-checkbox
                              v-if="field.value.type === 'bool'"
                              v-model="editedItem.fields[field.name]"
                              :label="field.label"
                              :hint="field.description"
                              persistent-hint
                            ></v-checkbox>
                            <v-text-field
                              v-if="field.value.type === 'port'"
                              v-model="editedItem.fields[field.name]"
                              :label="field.label"
                              :hint="field.description"
                              :rules="portNumber"
                              persistent-hint
                            ></v-text-field>
                            <v-text-field
                              v-if="field.value.type === 'ip'"
                              v-model="editedItem.fields[field.name]"
                              :label="field.label"
                              :hint="field.description"
                              :rules="ipRules"
                              persistent-hint
                            ></v-text-field>
                            <v-text-field
                              v-if="field.value.type === 'cidr'"
                              v-model="editedItem.fields[field.name]"
                              :label="field.label"
                              :hint="field.description"
                              :rules="ipRules"
                              persistent-hint
                            ></v-text-field>
                            <v-text-field
                                v-if="field.value.type == 'integer'"
                                v-model="editedItem.fields[field.name]"
                                :label="field.label"
                                :hint="field.description"
                                :rules="[(v) => withinRange(v, field.value.min ?? null, field.value.max ?? null)]"
                                persistent-hint
                            ></v-text-field>
                            <div v-if="field.value.type === 'port_range' && Array.isArray(editedItem.fields[field.name])">
                              <v-text-field
                                v-model="editedItem.fields[field.name][0]"
                                label="Port Start (Inclusive)"
                                :hint="field.description"
                                :rules="portNumber"
                                persistent-hint
                              ></v-text-field>
                              <v-text-field
                                v-model="editedItem.fields[field.name][1]"
                                label="Port End (Inclusive)"
                                :hint="field.description"
                                :rules="portNumber"
                                persistent-hint
                              ></v-text-field>
                            </div>
                          </v-col>
                        </template>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="closeDialog"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="saveFilter">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import ipAddr from "ip-address";

export default {
  mounted() {
    this.fetchAvailableFilters();
    this.fetchFilters();
  },

  data() {
    return {
      selected: [],
      headers: [
        { text: "ID", sortable: false, align: "left", value: "id" },
        { text: "Name", value: "label" },
        { text: "Settings", value: "settings" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      filterOptions: {},
      filters: [],
      errors: [],
      loading: true,
      dialog: false,
      editedItem: {
        id: "",
        fields: {}
      },
      defaultItem: {
        id: "",
        fields: {}
      },
      search: "",
      selectedFilterType: null,
      currentFields: [],
      currentDescription: "",
      filterTypes: [],
      filtersAvailable: [],
      ipRules: [
        (v) => !!v || "IP address is required",
        (v) =>
          (!!v &&
            (new ipAddr.Address4(v).isValid() ||
              new ipAddr.Address6(v).isValid())) ||
          "IP address is invalid",
      ],
      portNumber: [
        (v) =>
          !v ||
          (parseInt(v) !== NaN &&
            parseInt(v) > 0 &&
            parseInt(v) < 65536 &&
            v === parseInt(v).toString()) ||
          "Must be between 1 and 65535",
      ],
      withinRange: (v, min, max) =>
        !v ||
        (!min && !max) ||
        (parseInt(v) !== NaN && parseInt(v) >= min && parseInt(v) <= max) ||
        `Must be between ${min} and ${max}`,
      itemsPerPage: 10,
      totalFilters: 0,
      currentPage: 1,
    };
  },

  watch: {
    dialog(val) {
      if (!val) this.closeDialog();
    },
  },

  computed: {
    formTitle() {
      return this.editedItem.id === "" ? "New Filter" : "Edit Filter";
    },
  },

  methods: {
    fetchAvailableFilters() {
      api.query("v2/filters/available")
        .then((response) => {
          for (const key in response.data.filters) {
            const filter = response.data.filters[key];
            const tmp = {
              name: filter.name, // API name
              label: filter.label, // Label for display
              description: filter.description,
              fields: filter.fields,
            };
            this.filtersAvailable.push(tmp);
          }
        })
        .catch((error) => (this.errors = [error]))
        .finally(() => (this.loading = false));
    },

    getLabelFromName(name) {
      for(let i = 0; i < this.filtersAvailable.length; i++) {
        if (this.filtersAvailable[i].name === name) {
          return this.filtersAvailable[i].label || null;
        }
      }
      return null;
    },

    fetchFilters() {
      this.loading = true;
      api.query(`v2/filters?page=${this.currentPage}&size=${this.itemsPerPage}`)
        .then((response) => {
          this.filters = response.data.items;
          this.filters.forEach((item, _) => {
            item.label = this.getLabelFromName(item.name);
          });
          this.totalFilters = response.data.total;
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchFilters();
    },

    onItemsPerPageChange(size) {
      this.itemsPerPage = size;
      this.currentPage = 1; // Reset to the first page
      this.fetchFilters();
    },

    onFilterTypeChange(filterName) {
      const filter = this.filtersAvailable.find(f => f.name === filterName);
      this.selectedFilterType = filter.name;

      this.editedItem = null;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.currentFields = filter.fields;
      this.currentDescription = filter.description;

      // Initialize port range fields
      for (const key in filter.fields) {
        if (filter.fields[key].name === 'port_range') {
          this.editedItem.fields[filter.fields[key].name] = [null, null];
        }
      }
    },

    closeDialog() {
      this.dialog = false;
      this.editedItem = {
        id: "",
        fields: {},
      };
      this.selectedFilterType = null;
      this.currentFields = [];
    },

    saveFilter() {
      if (!this.$refs.newForm.validate()) return;

      api.post(`v2/filters/${this.selectedFilterType}`, this.editedItem.fields)
        .then((_) => {
          this.fetchFilters();
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
        .finally(() => {
          this.closeDialog();
        });
    },

    deleteItem(item) {
      if (confirm("Are you sure you want to delete this item?")) {
        api.delete(`v2/filters/${item.name}/${item.id}`).then(() => {
          this.fetchFilters();
        });
      }
    },

    filteredSettings(settings) {
      return Object.entries(settings).filter(([key, value]) => value !== null);
    },    
  },
};
</script>
